import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Save, Image } from "lucide-react";

export default function AdminCities() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: cities, isLoading } = useQuery({
    queryKey: ["admin-cities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cities")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});

  const startEdit = (city: any) => {
    setEditing(city.id);
    setForm({
      hero_image_url: city.hero_image_url ?? "",
      hero_image_alt: city.hero_image_alt ?? "",
      hero_image_credit: city.hero_image_credit ?? "",
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("cities")
        .update({
          hero_image_url: form.hero_image_url || null,
          hero_image_alt: form.hero_image_alt || null,
          hero_image_credit: form.hero_image_credit || null,
        })
        .eq("id", editing!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-cities"] });
      setEditing(null);
      toast({ title: "Ciudad actualizada" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <AdminLayout requiredModule="ciudades" requiredPermission="can_view">
      <div>
        <h1 className="mb-6 font-display text-2xl font-bold">Gestión de Ciudades</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Configura la imagen hero de cada ciudad que aparece en el explorador de la home.
        </p>

        {isLoading ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cities?.map((city) => (
              <Card key={city.id}>
                <CardContent className="p-4">
                  {/* Thumbnail */}
                  <div className="aspect-video rounded-md overflow-hidden bg-muted mb-3">
                    {city.hero_image_url ? (
                      <img src={city.hero_image_url} alt={city.hero_image_alt ?? city.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <Image className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>

                  <p className="text-sm font-semibold">{city.name}</p>
                  <p className="text-xs text-muted-foreground mb-2">/{city.slug}</p>

                  {editing === city.id ? (
                    <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }} className="space-y-2 mt-2">
                      <div className="space-y-1">
                        <Label className="text-xs">URL de imagen</Label>
                        <Input
                          value={form.hero_image_url}
                          onChange={(e) => setForm((p) => ({ ...p, hero_image_url: e.target.value }))}
                          placeholder="https://..."
                          className="text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Alt text</Label>
                        <Input
                          value={form.hero_image_alt}
                          onChange={(e) => setForm((p) => ({ ...p, hero_image_alt: e.target.value }))}
                          placeholder="Descripción de la imagen"
                          className="text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Crédito</Label>
                        <Input
                          value={form.hero_image_credit}
                          onChange={(e) => setForm((p) => ({ ...p, hero_image_credit: e.target.value }))}
                          placeholder="Fotógrafo / fuente"
                          className="text-xs"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" size="sm" disabled={saveMutation.isPending}>
                          <Save className="mr-1 h-3 w-3" /> Guardar
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                      </div>
                    </form>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-1 w-full" onClick={() => startEdit(city)}>
                      Editar imagen
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
